import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography, Link, Skeleton } from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import PercentIcon from '@mui/icons-material/Percent';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getOrganizationStatistics } from '../../services/api';
import {
  DASHBOARD_PENDING_TASKS_TTL_MS,
  fetchWithDashboardCache,
  getOrganizationStatsCacheKey,
  readDashboardCache,
} from '../../utils/dashboardDataCache';
import {
  buildMobileHomeDashboardStats,
  formatPerformanceCurrency,
  formatPerformancePercent,
} from '../../utils/performanceMetrics';
import {
  mobileSectionSubtleSx,
  mobileSectionTitleSx,
  mobileNavGridItemLabelSx,
} from '../layout/mobile/mobileAppTheme';
import {
  getMobileHomeDatePillSx,
  getMobileHomeDividerSx,
  getMobileHomeInventoryBarSegmentSx,
  getMobileHomeInventoryBarTrackSx,
  getMobileHomeInventoryChipLabelSx,
  getMobileHomeInventoryChipSx,
  getMobileHomeInventoryChipValueSx,
  getMobileHomeInventoryPanelSx,
  getMobileHomeMetricContentSx,
  getMobileHomeMetricGridSpacing,
  getMobileHomeMetricGridSx,
  getMobileHomeMetricIconSx,
  getMobileHomeMetricLabelSx,
  getMobileHomeMetricTileSx,
  getMobileHomeMetricValueSx,
  getMobileHomeOverviewPanelSx,
  getMobileHomeQuickActionIconSx,
  getMobileHomeQuickActionSx,
  getMobileHomeSectionChipDotSx,
  getMobileHomeSectionChipSx,
  getMobileHomeViewAllSx,
} from '../../utils/mobileHomeDashboardLayout';
import { getMobileNavGroups } from '../../config/mobileAppNavigation';

const ROOM_STATUS_COLORS = {
  direct: '#66d4a8',
  ota: '#f8e082',
  available: '#60a5fa',
  overbooked: '#ff4861',
};

const STATS_CACHE_TTL_MS = DASHBOARD_PENDING_TASKS_TTL_MS;

const metricSkeletonSx = {
  bgcolor: 'rgba(148,163,184,0.16)',
  borderRadius: 0.75,
};

function MetricValueSkeleton({ width = '72%', height = 18 }) {
  return (
    <Skeleton
      variant="text"
      width={width}
      height={height}
      animation="wave"
      sx={metricSkeletonSx}
    />
  );
}

function InventoryValueSkeleton() {
  return (
    <Skeleton
      variant="text"
      width={24}
      height={16}
      animation="wave"
      sx={metricSkeletonSx}
    />
  );
}

function MetricTile({ metric, showSkeleton }) {
  const Icon = metric.icon;

  return (
    <Box sx={getMobileHomeMetricTileSx(metric.color)}>
      <Box sx={getMobileHomeMetricIconSx(metric.color)}>
        <Icon />
      </Box>
      <Box sx={getMobileHomeMetricContentSx()}>
        {showSkeleton ? (
          <MetricValueSkeleton width={metric.skeletonWidth} height={22} />
        ) : (
          <Typography sx={getMobileHomeMetricValueSx()}>{metric.value}</Typography>
        )}
        <Typography sx={getMobileHomeMetricLabelSx()}>{metric.label}</Typography>
      </Box>
    </Box>
  );
}

function MetricSection({ title, metrics, showSkeleton }) {
  return (
    <Box>
      <Box sx={getMobileHomeSectionChipSx()}>
        <Box sx={getMobileHomeSectionChipDotSx()} />
        {title}
      </Box>
      <Grid
        container
        spacing={getMobileHomeMetricGridSpacing()}
        sx={getMobileHomeMetricGridSx()}
      >
        {metrics.map((metric) => (
          <Grid item xs={4} key={metric.label} sx={{ display: 'flex', minWidth: 0 }}>
            <MetricTile metric={metric} showSkeleton={showSkeleton} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function MobileHomeDashboard() {
  const navigate = useNavigate();
  const navGroups = useMemo(() => getMobileNavGroups(), []);
  const djangoContext = window.DJANGO_CONTEXT || {};
  const selectedHotel = djangoContext.selectedHotel || null;
  const todayStr = moment().format('YYYY-MM-DD');
  const monthStart = moment().startOf('month').format('YYYY-MM-DD');

  const statsCacheKey = selectedHotel?.id
    ? getOrganizationStatsCacheKey(selectedHotel.id, monthStart, todayStr)
    : null;
  const initialCachedResponse = statsCacheKey ? readDashboardCache(statsCacheKey) : null;
  const [stats, setStats] = useState(() => (
    initialCachedResponse ? buildMobileHomeDashboardStats(initialCachedResponse, todayStr) : null
  ));
  const [loading, setLoading] = useState(Boolean(selectedHotel?.id && !initialCachedResponse));
  const requestIdRef = useRef(0);
  const statsRef = useRef(stats);
  statsRef.current = stats;

  const loadStats = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
    if (!selectedHotel?.id) {
      setStats(null);
      setLoading(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!silent && !statsRef.current) {
      setLoading(true);
    }

    try {
      const cacheKey = getOrganizationStatsCacheKey(selectedHotel.id, monthStart, todayStr);
      const orgResponse = await fetchWithDashboardCache(
        cacheKey,
        () => getOrganizationStatistics(selectedHotel.id, monthStart, todayStr),
        { ttl: STATS_CACHE_TTL_MS, forceRefresh },
      );
      if (requestIdRef.current !== requestId) return;

      const nextStats = buildMobileHomeDashboardStats(orgResponse, todayStr);
      setStats(nextStats);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      if (!statsRef.current) {
        setStats(null);
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [monthStart, selectedHotel?.id, todayStr]);

  useEffect(() => {
    loadStats({ silent: Boolean(statsRef.current) });
  }, [loadStats, selectedHotel?.id, todayStr]);

  const showMetricSkeleton = loading && !stats;

  const roomStatusItems = useMemo(() => {
    if (showMetricSkeleton) {
      return [
        { label: 'Direct', value: 0, color: ROOM_STATUS_COLORS.direct },
        { label: 'OTA', value: 0, color: ROOM_STATUS_COLORS.ota },
        { label: 'Available', value: 0, color: ROOM_STATUS_COLORS.available },
      ];
    }

    const items = [
      { label: 'Direct', value: stats?.directBooked || 0, color: ROOM_STATUS_COLORS.direct },
      { label: 'OTA', value: stats?.onlineBooked || 0, color: ROOM_STATUS_COLORS.ota },
      { label: 'Available', value: stats?.available || 0, color: ROOM_STATUS_COLORS.available },
    ];
    if (Number(stats?.overbooked || 0) > 0) {
      items.push({
        label: 'Overbooked',
        value: stats.overbooked,
        color: ROOM_STATUS_COLORS.overbooked,
      });
    }
    return items;
  }, [showMetricSkeleton, stats]);

  const totalStatus =
    roomStatusItems.reduce((sum, item) => sum + Number(item.value || 0), 0) ||
    Number(stats?.totalRooms || 0) ||
    1;

  const todayMetrics = [
    {
      label: 'Booked / Total',
      value: stats ? `${stats.bookedRooms}/${stats.totalRooms}` : '',
      skeletonWidth: '78%',
      icon: HotelIcon,
      color: ROOM_STATUS_COLORS.available,
    },
    {
      label: "Today's Revenue",
      value: stats ? formatPerformanceCurrency(stats.todayRevenue) : '',
      skeletonWidth: '70%',
      icon: PaidIcon,
      color: ROOM_STATUS_COLORS.direct,
    },
    {
      label: 'Occupancy',
      value: stats ? formatPerformancePercent(stats.occupancy) : '',
      skeletonWidth: '62%',
      icon: BarChartIcon,
      color: ROOM_STATUS_COLORS.direct,
    },
  ];

  const mtdMetrics = [
    {
      label: 'MTD Revenue',
      value: stats ? formatPerformanceCurrency(stats.mtdRevenue) : '',
      skeletonWidth: '82%',
      icon: TrendingUpIcon,
      color: '#a78bfa',
    },
    {
      label: 'MTD Expenses',
      value: stats ? formatPerformanceCurrency(stats.mtdExpenses) : '',
      skeletonWidth: '68%',
      icon: TrendingDownIcon,
      color: ROOM_STATUS_COLORS.ota,
    },
    {
      label: 'MTD Occupancy',
      value: stats ? formatPerformancePercent(stats.mtdOccupancy) : '',
      skeletonWidth: '58%',
      icon: PercentIcon,
      color: ROOM_STATUS_COLORS.available,
    },
  ];

  const quickActionItems = [
    ...navGroups.quickActions.slice(0, 7),
    {
      title: 'More',
      icon: null,
      url: null,
      color: '#cbd5e1',
      isMore: true,
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        px: 1.25,
        pt: 0,
        pb: 0.25,
        gap: 0.45,
        overflowX: 'hidden',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        touchAction: 'pan-y',
        boxSizing: 'border-box',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148,163,184,0.45) transparent',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(148,163,184,0.45)',
          borderRadius: 999,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ ...mobileSectionTitleSx, fontSize: '0.98rem' }}>Today&apos;s Overview</Typography>
        <Typography component="span" sx={getMobileHomeDatePillSx()}>
          {moment().format('DD MMM YYYY')}
        </Typography>
      </Box>

      <Box sx={{ ...getMobileHomeOverviewPanelSx(), flexShrink: 0 }}>
        <MetricSection title="Today" metrics={todayMetrics} showSkeleton={showMetricSkeleton} />
        <Box sx={getMobileHomeDividerSx()} />
        <MetricSection title="This month" metrics={mtdMetrics} showSkeleton={showMetricSkeleton} />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.45 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
            <Typography sx={{ ...mobileSectionTitleSx, fontSize: '0.98rem' }}>Room Inventory</Typography>
            {!showMetricSkeleton && stats?.totalRooms != null && (
              <Typography
                component="span"
                sx={{
                  ...getMobileHomeDatePillSx(),
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontSize: '0.68rem',
                }}
              >
                <MeetingRoomOutlinedIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                {stats.totalRooms} rooms
              </Typography>
            )}
          </Box>
          <Link
            component="button"
            onClick={() => navigate('/dashboard/performance/')}
            sx={getMobileHomeViewAllSx()}
          >
            View All
          </Link>
        </Box>

        <Box sx={getMobileHomeInventoryPanelSx()}>
          {showMetricSkeleton ? (
            <Skeleton
              variant="rounded"
              height={16}
              animation="wave"
              sx={{ ...metricSkeletonSx, borderRadius: 999, mb: 1 }}
            />
          ) : (
            <Box sx={getMobileHomeInventoryBarTrackSx()}>
              {roomStatusItems.map((item) => (
                <Box
                  key={item.label}
                  sx={getMobileHomeInventoryBarSegmentSx(
                    item.color,
                    (Number(item.value || 0) / totalStatus) * 100
                  )}
                />
              ))}
            </Box>
          )}

          <Grid container spacing={0.6}>
            {roomStatusItems.map((item) => (
              <Grid item xs={roomStatusItems.length === 4 ? 3 : 4} key={item.label}>
                <Box sx={getMobileHomeInventoryChipSx(item.color)}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {showMetricSkeleton ? (
                      <InventoryValueSkeleton />
                    ) : (
                      <Typography sx={getMobileHomeInventoryChipValueSx()}>{item.value}</Typography>
                    )}
                    <Typography sx={getMobileHomeInventoryChipLabelSx()}>{item.label}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ ...mobileSectionTitleSx, fontSize: '0.98rem', mb: 0.45, flexShrink: 0 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={0.6} sx={{ alignContent: 'flex-start' }}>
          {quickActionItems.map((action) => {
            const Icon = action.icon;
            const key = action.isMore ? 'more' : action.title;
            return (
              <Grid item xs={3} key={key} sx={{ display: 'flex' }}>
                <Box
                  sx={{ ...getMobileHomeQuickActionSx(action.color), width: '100%' }}
                  onClick={() => {
                    if (action.isMore) {
                      window.dispatchEvent(new CustomEvent('roomnexa:mobile-more-open'));
                      return;
                    }
                    navigate(action.url);
                  }}
                >
                  <Box sx={getMobileHomeQuickActionIconSx(action.color, action.isMore)}>
                    {action.isMore ? (
                      <Typography sx={{ fontSize: '1.15rem', lineHeight: 1, fontWeight: 700 }}>...</Typography>
                    ) : (
                      <Icon sx={{ fontSize: 22 }} />
                    )}
                  </Box>
                  <Typography sx={mobileNavGridItemLabelSx}>
                    {action.title}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
